import { useEffect, useState } from 'react'
import { salesConfig } from './config'

type LicenseApiResponse = {
  activated?: boolean
  valid?: boolean
  deactivated?: boolean
  error?: string | null
  instance?: { id: string; name: string } | null
  license_key?: { status: string; expires_at: string | null; test_mode?: boolean }
  meta?: {
    product_name?: string
    variant_name?: string
    customer_email?: string
  }
}

type StoredLicense = {
  key: string
  email: string
  instanceId: string
  variantName: string
}

type LicenseStatus = 'idle' | 'checking' | 'active' | 'error'

const storageKey = 'catalogfix-license-v1'

const normalizeEmail = (value: string) => value.trim().toLowerCase()

function readStoredLicense(): StoredLicense | null {
  try {
    const value = localStorage.getItem(storageKey)
    return value ? JSON.parse(value) as StoredLicense : null
  } catch {
    return null
  }
}

function isExpectedPurchase(response: LicenseApiResponse, email: string) {
  return response.meta?.product_name === salesConfig.productName
    && salesConfig.allowedVariants.includes(response.meta?.variant_name as (typeof salesConfig.allowedVariants)[number])
    && normalizeEmail(response.meta?.customer_email ?? '') === normalizeEmail(email)
    && (salesConfig.allowTestLicenses || response.license_key?.test_mode !== true)
}

async function postLicenseAction(action: 'activate' | 'validate' | 'deactivate', values: Record<string, string>) {
  const response = await fetch(`/api/licenses/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body: new URLSearchParams(values),
  })
  const payload = await response.json().catch(() => ({})) as LicenseApiResponse
  if (!response.ok) throw new Error(payload.error || 'License service is temporarily unavailable.')
  return payload
}

export function useLicense() {
  const [stored, setStored] = useState<StoredLicense | null>(() => readStoredLicense())
  const [status, setStatus] = useState<LicenseStatus>(stored ? 'checking' : 'idle')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!stored) return
    let cancelled = false
    postLicenseAction('validate', { license_key: stored.key, instance_id: stored.instanceId })
      .then((response) => {
        if (cancelled) return
        if (response.valid && response.license_key?.status === 'active' && isExpectedPurchase(response, stored.email)) {
          setStatus('active')
          setMessage(`Pro active · ${stored.variantName}`)
        } else {
          localStorage.removeItem(storageKey)
          setStored(null)
          setStatus('error')
          setMessage(response.error || 'This license is no longer active.')
        }
      })
      .catch(() => {
        if (cancelled) return
        setStatus('error')
        setMessage('We could not verify the license. Check your connection and try again.')
      })
    return () => { cancelled = true }
  }, [stored])

  const activate = async (licenseKey: string, email: string) => {
    setStatus('checking')
    setMessage('Activating your license…')
    try {
      const response = await postLicenseAction('activate', {
        license_key: licenseKey.trim(),
        instance_name: `CatalogFix Browser ${crypto.randomUUID().slice(0, 8)}`,
      })
      if (!response.activated || !response.instance?.id) throw new Error(response.error || 'The license could not be activated.')
      if (!isExpectedPurchase(response, email)) {
        await postLicenseAction('deactivate', { license_key: licenseKey.trim(), instance_id: response.instance.id })
        throw new Error('The license and checkout email do not match a CatalogFix Pro purchase.')
      }
      const next: StoredLicense = {
        key: licenseKey.trim(),
        email: normalizeEmail(email),
        instanceId: response.instance.id,
        variantName: response.meta?.variant_name || 'Pro',
      }
      localStorage.setItem(storageKey, JSON.stringify(next))
      setStored(next)
      setStatus('active')
      setMessage(`Pro active · ${next.variantName}`)
    } catch (caught) {
      setStatus('error')
      setMessage(caught instanceof Error ? caught.message : 'The license could not be activated.')
    }
  }

  const deactivate = async () => {
    if (stored) {
      try {
        await postLicenseAction('deactivate', { license_key: stored.key, instance_id: stored.instanceId })
      } catch {
        // Clear local access even if the remote service is temporarily unavailable.
      }
    }
    localStorage.removeItem(storageKey)
    setStored(null)
    setStatus('idle')
    setMessage('')
  }

  return { isActive: status === 'active', status, message, activate, deactivate }
}
